// src/redux/scienceOfSelf/scienceOfSelfSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createScienceOfSelfAPI,
  getScienceOfSelfListAPI,
  getScienceOfSelfByIdAPI,
  updateScienceOfSelfAPI,
  deleteScienceOfSelfAPI,
  archiveScienceOfSelfAPI,
  addScienceOfSelfSessionAPI,
  getScienceOfSelfSessionsAPI,
  enableDisableToolAPI,
  enableDisableAssignmentAPI,
  enableDisableSupportDocumentAPI,
  getScienceOfSelfSessionDetailsAPI,
  enableDisableScienceOfSelfSessionAPI,
} from "./Scienceofselfapi";

// ==================== ORIGINAL THUNKS ====================

// ✅ Create Science of Self
export const createScienceOfSelf = createAsyncThunk(
  "scienceOfSelf/createScienceOfSelf",
  async (scienceData, { rejectWithValue }) => {
    try {
      const response = await createScienceOfSelfAPI(scienceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create science of self");
    }
  }
);

// ✅ Get Science of Self List
export const getScienceOfSelfList = createAsyncThunk(
  "scienceOfSelf/getScienceOfSelfList",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await getScienceOfSelfListAPI({ page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch science of self list");
    }
  }
);

// ✅ Get Science of Self by ID
export const getScienceOfSelfById = createAsyncThunk(
  "scienceOfSelf/getScienceOfSelfById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getScienceOfSelfByIdAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch science of self detail");
    }
  }
);

// ✅ Update Science of Self
export const updateScienceOfSelf = createAsyncThunk(
  "scienceOfSelf/updateScienceOfSelf",
  async ({ science_id, scienceData }, { rejectWithValue }) => {
    try {
      const response = await updateScienceOfSelfAPI(science_id, scienceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update science of self");
    }
  }
);

// ✅ Delete Science of Self
export const deleteScienceOfSelf = createAsyncThunk(
  "scienceOfSelf/deleteScienceOfSelf",
  async (science_id, { rejectWithValue }) => {
    try {
      const response = await deleteScienceOfSelfAPI(science_id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete science of self");
    }
  }
);

// ✅ Archive/Unarchive Science of Self
export const archiveScienceOfSelf = createAsyncThunk(
  "scienceOfSelf/archiveScienceOfSelf",
  async ({ science_id, is_archive }, { rejectWithValue }) => {
    try {
      const response = await archiveScienceOfSelfAPI(science_id, is_archive);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive science of self");
    }
  }
);

// ==================== SESSION MANAGEMENT THUNKS ====================

// ✅ Add Science of Self Session
export const addScienceOfSelfSession = createAsyncThunk(
  "scienceOfSelf/addScienceOfSelfSession",
  async (sessionData, { rejectWithValue }) => {
    try {
      const response = await addScienceOfSelfSessionAPI(sessionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add session");
    }
  }
);

// ✅ Get Science of Self Sessions
export const getScienceOfSelfSessions = createAsyncThunk(
  "scienceOfSelf/getScienceOfSelfSessions",
  async ({ science_id, page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await getScienceOfSelfSessionsAPI({ science_id, page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sessions");
    }
  }
);

// ✅ Enable/Disable Tool
export const enableDisableTool = createAsyncThunk(
  "scienceOfSelf/enableDisableTool",
  async (toolData, { rejectWithValue }) => {
    try {
      const response = await enableDisableToolAPI(toolData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update tool status");
    }
  }
);

// ✅ Enable/Disable Assignment
export const enableDisableAssignment = createAsyncThunk(
  "scienceOfSelf/enableDisableAssignment",
  async (assignmentData, { rejectWithValue }) => {
    try {
      const response = await enableDisableAssignmentAPI(assignmentData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update assignment status");
    }
  }
);

// ✅ Enable/Disable Support Document
export const enableDisableSupportDocument = createAsyncThunk(
  "scienceOfSelf/enableDisableSupportDocument",
  async (supportDocData, { rejectWithValue }) => {
    try {
      const response = await enableDisableSupportDocumentAPI(supportDocData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update support document status");
    }
  }
);

// ✅ Get Science of Self Session Details
export const getScienceOfSelfSessionDetails = createAsyncThunk(
  "scienceOfSelf/getScienceOfSelfSessionDetails",
  async ({ science_of_self_session_id, science_of_self_id }, { rejectWithValue }) => {
    try {
      const response = await getScienceOfSelfSessionDetailsAPI(
        science_of_self_session_id,
        science_of_self_id
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch session details");
    }
  }
);

// ✅ Enable/Disable Science of Self Session
// POST /science-of-self/sessions/enable
// Payload:
//   - science_of_self_id         : number  (required)
//   - science_of_self_session_id : number  (required)
//   - assign_status              : boolean (required) — true to enable, false to disable
//   - mark_completed             : boolean (optional) — directly mark session as completed
//     without requiring tool, assignment, or supported document APIs
export const enableDisableScienceOfSelfSession = createAsyncThunk(
  "scienceOfSelf/enableDisableScienceOfSelfSession",
  async (
    { science_of_self_id, science_of_self_session_id, assign_status, mark_completed = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await enableDisableScienceOfSelfSessionAPI({
        science_of_self_id,
        science_of_self_session_id,
        assign_status,
        mark_completed,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to enable/disable science of self session"
      );
    }
  }
);

// ==================== INITIAL STATE ====================
const initialState = {
  // Original states
  createScienceOfSelf:      { data: null, loading: false, error: null },
  getScienceOfSelfList:     { data: [], total: 0, loading: false, error: null },
  getScienceOfSelfById:     { data: null, loading: false, error: null },
  updateScienceOfSelf:      { data: null, loading: false, error: null },
  deleteScienceOfSelf:      { data: null, loading: false, error: null },
  archiveScienceOfSelf:     { data: null, loading: false, error: null },

  // Session related states
  addScienceOfSelfSession:              { data: null, loading: false, error: null },
  getScienceOfSelfSessions:             { data: [], total: 0, loading: false, error: null },
  enableDisableTool:                    { data: null, loading: false, error: null },
  enableDisableAssignment:              { data: null, loading: false, error: null },
  enableDisableSupportDocument:         { data: null, loading: false, error: null },
  getScienceOfSelfSessionDetails:       { data: null, loading: false, error: null },
  enableDisableScienceOfSelfSession:    { data: null, loading: false, error: null },
};

// ==================== SLICE ====================
const scienceOfSelfSlice = createSlice({
  name: "scienceOfSelf",
  initialState,
  reducers: {
    // ---------- Original clear actions ----------
    clearCreateScienceOfSelf: (state) => {
      state.createScienceOfSelf.data = null;
      state.createScienceOfSelf.error = null;
    },
    clearScienceOfSelfList: (state) => {
      state.getScienceOfSelfList.data = [];
      state.getScienceOfSelfList.error = null;
      state.getScienceOfSelfList.total = 0;
    },
    clearScienceOfSelfById: (state) => {
      state.getScienceOfSelfById.data = null;
      state.getScienceOfSelfById.error = null;
    },
    clearUpdateScienceOfSelf: (state) => {
      state.updateScienceOfSelf.data = null;
      state.updateScienceOfSelf.error = null;
    },
    clearDeleteScienceOfSelf: (state) => {
      state.deleteScienceOfSelf.data = null;
      state.deleteScienceOfSelf.error = null;
    },
    clearArchiveScienceOfSelf: (state) => {
      state.archiveScienceOfSelf.data = null;
      state.archiveScienceOfSelf.error = null;
    },

    // ---------- Session clear actions ----------
    clearAddScienceOfSelfSession: (state) => {
      state.addScienceOfSelfSession.data = null;
      state.addScienceOfSelfSession.error = null;
    },
    clearScienceOfSelfSessions: (state) => {
      state.getScienceOfSelfSessions.data = [];
      state.getScienceOfSelfSessions.error = null;
      state.getScienceOfSelfSessions.total = 0;
    },
    clearEnableDisableTool: (state) => {
      state.enableDisableTool.data = null;
      state.enableDisableTool.error = null;
    },
    clearEnableDisableAssignment: (state) => {
      state.enableDisableAssignment.data = null;
      state.enableDisableAssignment.error = null;
    },
    clearEnableDisableSupportDocument: (state) => {
      state.enableDisableSupportDocument.data = null;
      state.enableDisableSupportDocument.error = null;
    },
    clearScienceOfSelfSessionDetails: (state) => {
      state.getScienceOfSelfSessionDetails.data = null;
      state.getScienceOfSelfSessionDetails.error = null;
    },
    clearEnableDisableScienceOfSelfSession: (state) => {
      state.enableDisableScienceOfSelfSession.data = null;
      state.enableDisableScienceOfSelfSession.error = null;
    },
  },
  extraReducers: (builder) => {

    // ==================== ORIGINAL REDUCERS ====================

    // ---------- Create Science of Self ----------
    builder
      .addCase(createScienceOfSelf.pending, (state) => {
        state.createScienceOfSelf.loading = true;
        state.createScienceOfSelf.error = null;
      })
      .addCase(createScienceOfSelf.fulfilled, (state, action) => {
        state.createScienceOfSelf.loading = false;
        state.createScienceOfSelf.data = action.payload?.data || action.payload;
      })
      .addCase(createScienceOfSelf.rejected, (state, action) => {
        state.createScienceOfSelf.loading = false;
        state.createScienceOfSelf.error = action.payload;
      });

    // ---------- Get Science of Self List ----------
    builder
      .addCase(getScienceOfSelfList.pending, (state) => {
        state.getScienceOfSelfList.loading = true;
        state.getScienceOfSelfList.error = null;
      })
      .addCase(getScienceOfSelfList.fulfilled, (state, action) => {
        state.getScienceOfSelfList.loading = false;
        state.getScienceOfSelfList.data = action.payload?.data || [];
        state.getScienceOfSelfList.total = action.payload?.total || 0;
      })
      .addCase(getScienceOfSelfList.rejected, (state, action) => {
        state.getScienceOfSelfList.loading = false;
        state.getScienceOfSelfList.error = action.payload;
      });

    // ---------- Get Science of Self by ID ----------
    builder
      .addCase(getScienceOfSelfById.pending, (state) => {
        state.getScienceOfSelfById.loading = true;
        state.getScienceOfSelfById.error = null;
      })
      .addCase(getScienceOfSelfById.fulfilled, (state, action) => {
        state.getScienceOfSelfById.loading = false;
        state.getScienceOfSelfById.data = action.payload?.data || action.payload;
      })
      .addCase(getScienceOfSelfById.rejected, (state, action) => {
        state.getScienceOfSelfById.loading = false;
        state.getScienceOfSelfById.error = action.payload;
      });

    // ---------- Update Science of Self ----------
    builder
      .addCase(updateScienceOfSelf.pending, (state) => {
        state.updateScienceOfSelf.loading = true;
        state.updateScienceOfSelf.error = null;
      })
      .addCase(updateScienceOfSelf.fulfilled, (state, action) => {
        state.updateScienceOfSelf.loading = false;
        state.updateScienceOfSelf.data = action.payload?.data || action.payload;
      })
      .addCase(updateScienceOfSelf.rejected, (state, action) => {
        state.updateScienceOfSelf.loading = false;
        state.updateScienceOfSelf.error = action.payload;
      });

    // ---------- Delete Science of Self ----------
    builder
      .addCase(deleteScienceOfSelf.pending, (state) => {
        state.deleteScienceOfSelf.loading = true;
        state.deleteScienceOfSelf.error = null;
      })
      .addCase(deleteScienceOfSelf.fulfilled, (state, action) => {
        state.deleteScienceOfSelf.loading = false;
        state.deleteScienceOfSelf.data = action.payload?.data || action.payload;
      })
      .addCase(deleteScienceOfSelf.rejected, (state, action) => {
        state.deleteScienceOfSelf.loading = false;
        state.deleteScienceOfSelf.error = action.payload;
      });

    // ---------- Archive Science of Self ----------
    builder
      .addCase(archiveScienceOfSelf.pending, (state) => {
        state.archiveScienceOfSelf.loading = true;
        state.archiveScienceOfSelf.error = null;
      })
      .addCase(archiveScienceOfSelf.fulfilled, (state, action) => {
        state.archiveScienceOfSelf.loading = false;
        state.archiveScienceOfSelf.data = action.payload?.data || action.payload;
      })
      .addCase(archiveScienceOfSelf.rejected, (state, action) => {
        state.archiveScienceOfSelf.loading = false;
        state.archiveScienceOfSelf.error = action.payload;
      });

    // ==================== SESSION MANAGEMENT REDUCERS ====================

    // ---------- Add Science of Self Session ----------
    builder
      .addCase(addScienceOfSelfSession.pending, (state) => {
        state.addScienceOfSelfSession.loading = true;
        state.addScienceOfSelfSession.error = null;
      })
      .addCase(addScienceOfSelfSession.fulfilled, (state, action) => {
        state.addScienceOfSelfSession.loading = false;
        state.addScienceOfSelfSession.data = action.payload?.data || action.payload;
      })
      .addCase(addScienceOfSelfSession.rejected, (state, action) => {
        state.addScienceOfSelfSession.loading = false;
        state.addScienceOfSelfSession.error = action.payload;
      });

    // ---------- Get Science of Self Sessions ----------
    builder
      .addCase(getScienceOfSelfSessions.pending, (state) => {
        state.getScienceOfSelfSessions.loading = true;
        state.getScienceOfSelfSessions.error = null;
      })
      .addCase(getScienceOfSelfSessions.fulfilled, (state, action) => {
        state.getScienceOfSelfSessions.loading = false;
        state.getScienceOfSelfSessions.data = action.payload?.data || [];
        state.getScienceOfSelfSessions.total = action.payload?.total || 0;
      })
      .addCase(getScienceOfSelfSessions.rejected, (state, action) => {
        state.getScienceOfSelfSessions.loading = false;
        state.getScienceOfSelfSessions.error = action.payload;
      });

    // ---------- Enable/Disable Tool ----------
    builder
      .addCase(enableDisableTool.pending, (state) => {
        state.enableDisableTool.loading = true;
        state.enableDisableTool.error = null;
      })
      .addCase(enableDisableTool.fulfilled, (state, action) => {
        state.enableDisableTool.loading = false;
        state.enableDisableTool.data = action.payload?.data || action.payload;
      })
      .addCase(enableDisableTool.rejected, (state, action) => {
        state.enableDisableTool.loading = false;
        state.enableDisableTool.error = action.payload;
      });

    // ---------- Enable/Disable Assignment ----------
    builder
      .addCase(enableDisableAssignment.pending, (state) => {
        state.enableDisableAssignment.loading = true;
        state.enableDisableAssignment.error = null;
      })
      .addCase(enableDisableAssignment.fulfilled, (state, action) => {
        state.enableDisableAssignment.loading = false;
        state.enableDisableAssignment.data = action.payload?.data || action.payload;
      })
      .addCase(enableDisableAssignment.rejected, (state, action) => {
        state.enableDisableAssignment.loading = false;
        state.enableDisableAssignment.error = action.payload;
      });

    // ---------- Enable/Disable Support Document ----------
    builder
      .addCase(enableDisableSupportDocument.pending, (state) => {
        state.enableDisableSupportDocument.loading = true;
        state.enableDisableSupportDocument.error = null;
      })
      .addCase(enableDisableSupportDocument.fulfilled, (state, action) => {
        state.enableDisableSupportDocument.loading = false;
        state.enableDisableSupportDocument.data = action.payload?.data || action.payload;
      })
      .addCase(enableDisableSupportDocument.rejected, (state, action) => {
        state.enableDisableSupportDocument.loading = false;
        state.enableDisableSupportDocument.error = action.payload;
      });

    // ---------- Get Science of Self Session Details ----------
    builder
      .addCase(getScienceOfSelfSessionDetails.pending, (state) => {
        state.getScienceOfSelfSessionDetails.loading = true;
        state.getScienceOfSelfSessionDetails.error = null;
      })
      .addCase(getScienceOfSelfSessionDetails.fulfilled, (state, action) => {
        state.getScienceOfSelfSessionDetails.loading = false;
        state.getScienceOfSelfSessionDetails.data = action.payload?.data || action.payload;
      })
      .addCase(getScienceOfSelfSessionDetails.rejected, (state, action) => {
        state.getScienceOfSelfSessionDetails.loading = false;
        state.getScienceOfSelfSessionDetails.error = action.payload;
      });

    // ---------- Enable/Disable Science of Self Session ----------
    builder
      .addCase(enableDisableScienceOfSelfSession.pending, (state) => {
        state.enableDisableScienceOfSelfSession.loading = true;
        state.enableDisableScienceOfSelfSession.error = null;
      })
      .addCase(enableDisableScienceOfSelfSession.fulfilled, (state, action) => {
        state.enableDisableScienceOfSelfSession.loading = false;
        state.enableDisableScienceOfSelfSession.data = action.payload?.data || action.payload;
      })
      .addCase(enableDisableScienceOfSelfSession.rejected, (state, action) => {
        state.enableDisableScienceOfSelfSession.loading = false;
        state.enableDisableScienceOfSelfSession.error = action.payload;
      });
  },
});

// ==================== EXPORTS ====================
export const {
  // Original actions
  clearCreateScienceOfSelf,
  clearScienceOfSelfList,
  clearScienceOfSelfById,
  clearUpdateScienceOfSelf,
  clearDeleteScienceOfSelf,
  clearArchiveScienceOfSelf,
  // Session actions
  clearAddScienceOfSelfSession,
  clearScienceOfSelfSessions,
  clearEnableDisableTool,
  clearEnableDisableAssignment,
  clearEnableDisableSupportDocument,
  clearScienceOfSelfSessionDetails,
  clearEnableDisableScienceOfSelfSession,
} = scienceOfSelfSlice.actions;

export default scienceOfSelfSlice.reducer;